import { usePerson } from '../contexts/PersonContext'
import { useState } from 'react'
import { useActivity } from '../hooks/useFriends'
import { useCircles } from '../hooks/useCircles'
import { Button } from '../components/ui/Button'
import { ActivityCard } from '../components/home/ActivityCard'
import { FamilyCard } from '../components/home/FamilyCard'
import { SafetyAlertCard } from '../components/home/SafetyAlertCard'
import { OpenRoomsSection } from '../components/rooms/OpenRoomsSection'
import { CommunityCreationActions } from '../components/home/CommunityCreationActions'
import { homeGreeting } from '../lib/homeGreeting'

/**
 * Parent home assembly (COTC_FriendsApprovals R4 / DESIGN §3.1). Greeting + activity card
 * (with amber pending banner) + family card (hidden solo) + safety alert. Standing card is owned
 * by COTC_Circles and slotted by the shell/lead.
 */
export function HomeParentPage() {
  const { guardianFirst, isFresh, isSolo, people, accountHolderId, counters, refresh } = usePerson()
  const activity = useActivity()
  const circles = useCircles()
  const [refreshing, setRefreshing] = useState(false)

  const activities = activity.data ?? []
  const circleCount = circles.isSuccess ? circles.data.length : null

  async function refreshHome() {
    setRefreshing(true)
    try { await Promise.allSettled([refresh(), activity.refetch(), circles.refetch()]) }
    finally { setRefreshing(false) }
  }

  const { title, kicker } = homeGreeting(new Date(), guardianFirst)

  return (
    <div className="animate-rise px-5 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-fainter">{kicker}</div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink sm:text-[38px]">{title}</h1>
        <Button variant="outline" size="sm" disabled={refreshing} onClick={() => void refreshHome()}>{refreshing ? 'Refreshing…' : 'Refresh home'}</Button>
      </div>
      <p className="mt-3 max-w-[62ch] text-[15px] leading-relaxed text-ink-muted">
        {isFresh && circleCount === 0
          ? 'Your account is set up. Nothing is shared yet — join or start a circle and this is where the activity will land.'
          : "Here's what's been happening across your circles."}
      </p>

      {circles.isError && <div role="alert" className="mt-5 rounded-btn border border-line-soft p-4 text-[13px] text-ink-muted">
        <p>Your circles could not be loaded.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => void circles.refetch()}>Retry circles</Button>
      </div>}
      <CommunityCreationActions />
      <OpenRoomsSection />
      <div className="mt-7 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <ActivityCard
          activities={activities}
          pendingCount={counters.pendingApprovals}
          circleCount={circleCount}
          loading={activity.isLoading}
          error={activity.isError}
          onRetry={() => void activity.refetch()}
        />
        {!isSolo && <FamilyCard people={people} accountHolderId={accountHolderId} />}
        <SafetyAlertCard openReports={counters.openReports} />
      </div>
    </div>
  )
}
